/**
 * DeletionPendingGate (1.34.0)
 *
 * Перехватывает пользователей с активным запросом на удаление аккаунта.
 * Если у юзера есть запрос (scheduled_anonymization_at в будущем,
 * cancelled_at = NULL, completed_at = NULL) — приложение блокируется,
 * показывается экран с двумя действиями:
 *
 *   • «Отменить удаление» — возвращает аккаунт в обычный режим
 *   • «Выйти» — logout, аккаунт остаётся в режиме удаления (через 30 дней
 *     будет анонимизирован)
 *
 * Гейт ставится в AuthBoundary ПОСЛЕ LegalConsentGate, чтобы:
 *   1) Сначала юзер принял условия (иначе нельзя ничего делать)
 *   2) Потом проверяем активный запрос на удаление
 *
 * Если запроса нет — пропускаем children (Outlet с маршрутами).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { C, F } from '../design-system/tokens';
import { Button } from '../design-system/atoms/Button';
import { Spinner } from '../design-system/atoms/Spinner';
import { useAuthStore } from '../domains/auth/authStore';
import {
  cancelAccountDeletion,
  fetchActiveDeletionRequest,
  type AccountDeletionRequest,
} from '../domains/legal/accountDeletion';

interface DeletionPendingGateProps {
  children: ReactNode;
}

type State =
  | { phase: 'loading' }
  | { phase: 'allowed' }
  | { phase: 'pending'; request: AccountDeletionRequest };

export function DeletionPendingGate({ children }: DeletionPendingGateProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  const [state, setState] = useState<State>({ phase: 'loading' });
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setState({ phase: 'allowed' });
      return;
    }

    let cancelled = false;
    setState({ phase: 'loading' });
    setError(null);

    void (async () => {
      try {
        const req = await fetchActiveDeletionRequest(user.id);
        if (cancelled) return;
        if (req) {
          setState({ phase: 'pending', request: req });
        } else {
          setState({ phase: 'allowed' });
        }
      } catch (e) {
        if (cancelled) return;
        console.error('[DeletionPendingGate] check failed:', e);
        // На ошибке БД пропускаем — лучше дать работать, чем заблокировать
        setState({ phase: 'allowed' });
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]);

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      await cancelAccountDeletion();
      setState({ phase: 'allowed' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отменить удаление');
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = async () => {
    setCancelling(true);
    try {
      await logout();
    } catch {
      // ignore — после logout уходим со страницы
    } finally {
      setCancelling(false);
    }
  };

  if (state.phase === 'loading') {
    return (
      <div style={{
        height: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: C.bg,
      }}>
        <Spinner label="Проверяем статус аккаунта..." />
      </div>
    );
  }

  if (state.phase === 'allowed') {
    return <>{children}</>;
  }

  // phase === 'pending'
  const scheduled = new Date(state.request.scheduled_anonymization_at);
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((scheduled.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  const scheduledStr = scheduled.toLocaleDateString('ru-RU', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div style={{
      height: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: C.bg,
      padding: 18,
      overflowY: 'auto',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 460,
        background: C.surf,
        border: `1px solid ${C.re}44`,
        borderRadius: 16,
        padding: '24px 22px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div>
          <div style={{
            fontFamily: F.head,
            fontWeight: 800,
            fontSize: 20,
            color: C.re,
            marginBottom: 8,
          }}>
            Аккаунт будет удалён
          </div>
          <div style={{ fontSize: 14, color: C.txt, lineHeight: 1.55 }}>
            Вы запросили удаление аккаунта. Полное удаление данных произойдёт
            автоматически <strong style={{ fontWeight: 700 }}>{scheduledStr}</strong>
            {daysLeft > 0 ? <> (через {daysLeft} {pluralizeDays(daysLeft)}).</> : '.'}
          </div>
        </div>

        <div style={{
          padding: 14,
          background: C.card,
          border: `1px solid ${C.b}`,
          borderRadius: 10,
          fontSize: 12,
          color: C.sub,
          lineHeight: 1.5,
        }}>
          <div style={{ marginBottom: 8 }}>
            <strong style={{ color: C.txt, fontWeight: 700 }}>До удаления вы можете:</strong>
          </div>
          <div>• Нажать «Отменить удаление» — аккаунт вернётся в обычный режим</div>
          <div>• Просто выйти — удаление выполнится автоматически в назначенную дату</div>
        </div>

        <div style={{
          padding: 14,
          background: `${C.re}0D`,
          border: `1px solid ${C.re}33`,
          borderRadius: 10,
          fontSize: 12,
          color: C.txt,
          lineHeight: 1.5,
        }}>
          <div style={{ marginBottom: 4, color: C.re, fontFamily: F.head, fontWeight: 700, fontSize: 11, letterSpacing: '.4px' }}>
            ПОСЛЕ УДАЛЕНИЯ
          </div>
          Имя, телефон, email и аватар будут заменены на «Удалённый пользователь».
          Восстановить аккаунт после этой даты невозможно. Связи с заявками
          в системе компании остаются, но без ваших персональных данных.
        </div>

        {error && (
          <div style={{
            padding: '10px 14px',
            background: C.reS,
            border: `1px solid ${C.re}55`,
            borderRadius: 10,
            color: C.re,
            fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            ch="Выйти"
            col="ghost"
            sz="md"
            onClick={() => void handleLogout()}
            disabled={cancelling}
          />
          <div style={{ flex: 1 }}>
            <Button
              fw
              ch="Отменить удаление"
              col="acc"
              sz="md"
              onClick={() => void handleCancel()}
              disabled={cancelling}
              loading={cancelling}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function pluralizeDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 19) return 'дней';
  if (mod10 === 1) return 'день';
  if (mod10 >= 2 && mod10 <= 4) return 'дня';
  return 'дней';
}
